import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface FullScreenLoaderProps {
  /** The specific thing being waited on: "Initialising application…". */
  message: string
  /** Determinate progress 0–100, when we have it. */
  progress?: number
}

/**
 * The "Initialising application…" / "Retrieving applicant information…" screens.
 *
 * These block the citizen while a non-idempotent POST is in flight, so the copy
 * asks them not to refresh — and `aria-live="polite"` means a screen-reader user
 * learns that something is happening rather than meeting silence.
 */
export function FullScreenLoader({ message, progress }: FullScreenLoaderProps) {
  const { t } = useTranslation('apply')

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-3 text-center"
    >
      <span className="flex size-8 items-center justify-center rounded-full bg-tile">
        <Loader2 aria-hidden="true" className="size-4 animate-spin text-text" />
      </span>

      <h1 className="text-h2">{message}</h1>
      <p className="text-body text-text-muted">{t('loading.doNotClose')}</p>

      <div
        className="h-1 w-full max-w-[280px] overflow-hidden rounded-full bg-border"
        aria-hidden="true"
      >
        <div
          className="h-full rounded-full bg-panel transition-[width] duration-500"
          style={{
            width: progress === undefined ? '45%' : `${Math.min(100, Math.max(0, progress))}%`,
          }}
        />
      </div>
    </div>
  )
}
